import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GetMediasAndFoldersResponse } from '@api/models/get-medias-and-folders-response';
import { Observable } from 'rxjs';

import { MediaFile } from '@models/media-files';
import { MediaDetails } from '@app/admin/media/media.model';
import { PaginationResult } from '@api/models/paginator';
import { ApiConfiguration } from '../api-configuration';
import { BaseService } from '../base-service';
import { MediaPricingAndInfoRequest, SendMediaTransferLinkEmailResponse } from '@app/admin/media-transfer/model/media-transfer.model';

@Injectable({
  providedIn: 'root',
})
export class MediaApi extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  static readonly GetMediaPath = '/media';

  getMediasAndFolders(params): Observable<any> {
    return this.http.get<GetMediasAndFoldersResponse>(`${this.rootUrl}${MediaApi.GetMediaPath}/media-and-folders`, { params });
  }
  getMediaById(id) {
    return this.http.get(`${this.rootUrl}${MediaApi.GetMediaPath}/${id}`);
  }
  editMedia(media): Observable<any> {
    return this.http.put(`${this.rootUrl}${MediaApi.GetMediaPath}`, media);
  }

  validateUniqueName(params): Observable<any> {
    return this.http.get(`${this.rootUrl}${MediaApi.GetMediaPath}/validate-unique-name`, { params });
  }

  removeMedias(params): Observable<any> {
    return this.http.delete(`${this.rootUrl}${MediaApi.GetMediaPath}`, { params });
  }

  mediaUploadImage(formData) {
    return this.http.post(`${this.rootUrl}${MediaApi.GetMediaPath}/images/upload`, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  mediaUploadVideo(formData) {
    return this.http.post(`${this.rootUrl}${MediaApi.GetMediaPath}/videos/upload`, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  getPendingMedias(params) {
    return this.http.get<any>(`${this.rootUrl}${MediaApi.GetMediaPath}/pending`, { params });
  }

  getMediaTransferAvailableFileCount(params) {
    return this.http.get<any>(`${this.rootUrl}${MediaApi.GetMediaPath}/validate-transfer-limit`, { params });
  }

  // PUT /Media/{mediaId}/pricing-and-info
  setMediaPricingAndInfo(mediaId: number, body: MediaPricingAndInfoRequest): Observable<any> {
    return this.http.put(`${this.rootUrl}${MediaApi.GetMediaPath}/${mediaId}/pricing-and-info`, body);
  }

  approveMediaTransfer(id: number, customerId: number) {
    return this.http.put(`${this.rootUrl}${MediaApi.GetMediaPath}/${id}/approve`, id, {
      params: {
        customerId: customerId,
      },
    });
  }

  rejectMediaTransfer(id) {
    return this.http.put(`${this.rootUrl}${MediaApi.GetMediaPath}/${id}/reject`, id);
  }

  crateMediaTransferLink(body) {
    return this.http.post(`${this.rootUrl}${MediaApi.GetMediaPath}/link-transfer-token`, body);
  }

  sendTransferLinkByEmail(transferLink: string, recipients: string[]): Observable<SendMediaTransferLinkEmailResponse> {
    return this.http.post<SendMediaTransferLinkEmailResponse>(`${this.rootUrl}${MediaApi.GetMediaPath}/send-transfer-link-email`, { transferLink, recipients });
  }

  uploadMediaForExternalUsers(body) {
    let params = new HttpParams();
    params = params.append('token', body.token);

    return this.http.post(`${this.rootUrl}${MediaApi.GetMediaPath}/transfer`, body.file, {
      params,
      reportProgress: true,
      observe: 'events',
    });
  }

  getMediaExtendedDetailsById(mediaId: string): Observable<MediaDetails> {
    return this.http.get<MediaDetails>(`${this.rootUrl}${MediaApi.GetMediaPath}/extended-details/${mediaId}`);
  }

  getUnusedMediaList(params): Observable<PaginationResult<MediaFile>> {
    return this.http.get<PaginationResult<MediaFile>>(`${this.rootUrl}${MediaApi.GetMediaPath}/unused-media`, { params });
  }
}
